import React from 'react'

import { usePermissionsContext } from '..'
import { TenantPermission } from '../types'
import { Unauthorized } from './Unauthorized'

/**
 * A guard component that controls access to protected content based on tenant permissions
 *
 * @component
 * @description
 * This component checks if the current user has the required tenant permission.
 * - Shows Unauthorized component if permission is missing
 * - Renders children if permission exists
 *
 * @param {TenantPermission} props.permissionName - The required tenant permission to check
 * @param {React.ReactNode} [props.children] - The content to render if permission exists
 *
 * @example
 * ```tsx
 * <TenantGuard permissionName="webedi:suppliercockpit:access">
 *   <SupplierCockpitComponent />
 * </TenantGuard>
 * ```
 *
 * @returns {JSX.Element | null} Returns null while loading, Unauthorized component if no permission,
 * or the children if permission exists
 */

type TenantGuardProps = {
  permissionName: TenantPermission
  children?: React.ReactNode
}

export const TenantGuard: React.FC<TenantGuardProps> = ({
  permissionName,
  children,
}) => {
  const { permissions } = usePermissionsContext()

  const hasPermission = permissions.data?.some(
    tenantPermission =>
      tenantPermission.name.toLowerCase() === permissionName.toLowerCase(),
  )

  if (!hasPermission) return <Unauthorized />

  return <>{children}</>
}
